import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { resolveAssetUrl } from "@/lib/assetUrl";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic'];

export default function ImageUpload({ 
  onUploadSuccess, 
  currentImageUrl = null,
  imageType = "product",
  className = ""
}) {
  const [preview, setPreview] = useState(currentImageUrl);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size: ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return false;
    }
    
    return true;
  };

  const handleFile = async (file) => {
    if (!validateFile(file)) return;
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    
    // Upload file
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const response = await axios.post(
        `${API}/upload/image?image_type=${imageType}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      // Backend returns a relative path like "/api/uploads/products/xxx.jpg".
      // Keep it relative — resolveAssetUrl() prepends BACKEND_URL at render
      // time. Concatenating ${API} here caused a `/api/api/uploads/...`
      // double-prefix that 404'd on every subsequent load.
      const imageUrl = response.data.url;

      setPreview(imageUrl);
      
      if (onUploadSuccess) {
        onUploadSuccess(imageUrl);
      }
      
      toast.success("Image uploaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Upload failed");
      setPreview(currentImageUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const clearImage = () => {
    setPreview(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (onUploadSuccess) {
      onUploadSuccess(null);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {preview ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200">
          <img
            src={preview?.startsWith("data:") || preview?.startsWith("blob:") ? preview : resolveAssetUrl(preview)}
            alt="Preview"
            className="w-full h-48 object-cover"
            onError={(e) => {
              // If a legacy product was saved with a broken doubled-api URL,
              // stop the browser from showing its jagged icon and hide the
              // preview slot so the empty-state uploader is visible again.
              e.currentTarget.style.display = "none";
            }}
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
          {!uploading && (
            <div className="absolute top-2 right-2 flex gap-2">
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={() => inputRef.current?.click()}
                className="h-8 w-8 bg-white/90 hover:bg-white"
              >
                <Upload className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={clearImage}
                className="h-8 w-8 bg-white/90 hover:bg-white text-red-500 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`
            border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
            transition-colors duration-200
            ${dragActive 
              ? 'border-tbc-gold bg-tbc-gold/5' 
              : 'border-gray-300 hover:border-tbc-navy hover:bg-gray-50'
            }
            ${uploading ? 'opacity-50 pointer-events-none' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="image-upload-dropzone"
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 text-gray-400 mx-auto mb-3 animate-spin" />
          ) : (
            <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          )}
          <p className="text-sm font-medium text-gray-700">
            {uploading ? 'Uploading...' : dragActive ? 'Drop image here' : 'Click or drag to upload'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            JPG, PNG, or HEIC (max 5MB)
          </p>
        </div>
      )}
      
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.heic,image/jpeg,image/png,image/heic"
        onChange={handleChange}
        className="hidden"
        data-testid="image-upload-input"
      />
    </div>
  );
}
